import { Button, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select } from "antd";
import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import "./style.css";
import API from "API";
import toastr from "toastr";
import { DeleteFilled } from "@ant-design/icons";
import FullMask from "components/layout/FullMask";
import RichEditor from "components/general/RichEditor";
import { v4 as uuidv4 } from "uuid";
import { correct_media } from "general_function";

const { TextArea } = Input;

const difficultyOptions = [
    { value: 0, label: "Dễ" },
    { value: 1, label: "Trung bình" },
    { value: 2, label: "Khó" },
]

function checkAnswers(answers) {
    for (const answer of answers) {
        if (!answer.trim()) {
            return false;
        }
    }
    return true;
}

const icons = [
    process.env.PUBLIC_URL + "/icons/minor.svg",
    process.env.PUBLIC_URL + "/icons/major.svg",
    process.env.PUBLIC_URL + "/icons/critical.svg",
]

function QuizzTest({ test, setTest, lessons }) {
    const [form] = Form.useForm();
    const initialValues = {
        lesson: [],
        number_of_quiz: 10,
        number_of_multichoice: 2,
    };

    const [quizzes, setQuizzes] = useState([]);
    const defaultSentence = 0;
    const [selectSentence, setSelectSentence] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleCloseModal = () => {
        setIsModalVisible(false);
    };

    const handleChangeAnswer = (index, value) => {
        const newQuizzes = [...quizzes];

        if (
            newQuizzes?.[selectSentence] &&
            newQuizzes[selectSentence].answers
        ) {
            newQuizzes[selectSentence].answers[index] = value;
        }
        setQuizzes(newQuizzes);
    };

    const handleChangeQuestion = (value) => {
        const newQuizzes = [...quizzes];

        if (newQuizzes?.[selectSentence]) {
            newQuizzes[selectSentence].question = value.text;
            newQuizzes[selectSentence].images = value.images;
        }
        setQuizzes(newQuizzes);
    };

    const handleChangeDifficulty = useCallback((value) => {
        setQuizzes(quizzes => {
            const newQuizzes = [...quizzes];

            if (newQuizzes?.[selectSentence]) {
                newQuizzes[selectSentence].difficulty = value;
            }
            return newQuizzes;
        });
    }, [selectSentence])

    const handleSubmit = () => {
        var error = false;
        
        const formData = new FormData();

        const newQuizzes = quizzes?.map(
            ({ id, question, answers, difficulty, correct_answers, images }, index) => {
                if (!question.trim()) {
                    toastr.error(`Câu hỏi ${index + 1} bị rỗng!`);
                    error = true;
                }
                for (const answer of answers) {
                    if (!answer.trim()) {
                        toastr.error(`Câu hỏi ${index + 1} có đáp án bị rỗng!`);
                        error = true;
                        break;
                    }
                }
                if (correct_answers.length === 0) {
                    toastr.error(`Câu hỏi ${index + 1} chưa có đáp án đúng!`);
                    error = true;
                }
                
                const codeImages = []
                for (const image of images) {
                    if (image.url || image.id) {
                        codeImages.push({
                            id: image.uid || image.id
                        });
                    } else {
                        const code = `image_${uuidv4()}`;
                        formData.append(code, image.originFileObj);
                        codeImages.push({code: code})
                    }
                }

                return {
                    id,
                    question,
                    answers,
                    difficulty,
                    correct_answers: correct_answers.sort(),
                    images: codeImages,
                };
            }
        );

        for (const diff of difficultyNumbers) {
            if (diff.warning) {
                error = true;
                toastr.error(`Số lượng câu hỏi ${diff.label} (${test[diff.key]}) nhiều hơn số lượng câu hỏi hiện có (${diff.count})!`);
            }
        }

        if (error) return;
        formData.append("test_id", test.id);
        formData.append("quizzes", JSON.stringify(newQuizzes));
        formData.append("number_of_easy", test.number_of_easy);
        formData.append("number_of_medium", test.number_of_medium);
        formData.append("number_of_hard", test.number_of_hard);

        setSubmitting(true);
        API.post("test-quizzes/", formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then((res) => {
                toastr.success("Lưu câu hỏi thành công");
                for (const quiz of res.data) {
                    for (const image of quiz.images) {
                        image.file = correct_media(image.file);
                    }
                }
                setQuizzes(res.data);
            })
            .catch((error) => {
                console.log(error);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const handleInitialAutoTest = (values) => {
        const newValues = {
            test_id: test?.id,
            ...values,
            number_of_multichoice: Number(values?.number_of_multichoice) || 2,
            number_of_quiz: Number(values?.number_of_quiz) || 10,
        };

        setSubmitting(true);
        API.post("generate-test-quiz/", newValues)
            .then(({ data }) => {
                setQuizzes(data?.quizzes);
                if (data?.no_content_lessons?.length > 0) {
                    toastr.warning(
                        `${data?.no_content_lessons
                            ?.map(({ name }) => {
                                return name;
                            })
                            .join(",")}`
                    );
                }
                toastr.success(
                    "Tạo bộ câu hỏi thành công"
                );
            })
            .catch((error) => {
                if (error?.response?.data?.error === "Please remove all quizzes in this test before generating quizzes with AI") {
                    toastr.error("Vui lòng xoá toàn bộ câu hỏi, và nhấn lưu trước khi khởi tạo câu hỏi bằng AI!");
                    return;
                }
                if (error?.response?.data?.error) {
                    toastr.error(error?.response?.data?.error);
                    return;
                }
                console.log(error);
            })
            .finally(() => {
                setSubmitting(false);
            });
        handleCloseModal();
    };

    useEffect(() => {
        if (test?.quizzes && test?.quizzes?.length > 0) {
            setQuizzes(test?.quizzes);
            setSelectSentence(defaultSentence);
        }
    }, [test]);

    const difficultyNumbers = useMemo(() => {
        const sample = { count: 0, warning: false }
        const result = [
            { ...sample, label: "Dễ", key: "number_of_easy" },
            { ...sample, label: "Trung bình", key: "number_of_medium" },
            { ...sample, label: "Khó", key: "number_of_hard" },
        ]
        for (const quiz of quizzes) {
            console.log(quiz.difficulty)
            result[quiz.difficulty].count += 1;
        }
        for (const re of result) {
            re.warning = test[re.key] > re.count;
        }
        return result;
    }, [quizzes, test])

    const handleChangeNumberOfDifficulty = useCallback((value, key) => {
        setTest(test => ({ ...test, [key]: value }))
    }, [setTest])

    return (
        <div style={{ position: "relative" }}>
            {submitting && <FullMask page />}
            <div style={{
                color: (test.number_of_easy + test.number_of_medium + test.number_of_hard > quizzes.length) ? "red" : null,
                fontStyle: "italic",
            }}>
                Tổng số câu hỏi: {test.number_of_easy + test.number_of_medium + test.number_of_hard}/{quizzes.length}
            </div>
            <Row gutter={16} style={{ marginBottom: 5 }}>
                {difficultyNumbers.map(item => <Col key={item.key} style={{
                    color: item.warning ? "red" : null
                }}>
                    {item.label} <br />
                    <InputNumber
                        step={1}
                        min={0}
                        className={item.warning ? "input-number-red" : ""}
                        value={test[item.key]}
                        onChange={(value) => handleChangeNumberOfDifficulty(value, item.key)}
                    /> / {item.count}
                </Col>)}
            </Row>
            <Row gutter={16}>
                <Col style={{ width: "130px", height: "400px" }}>
                    <div className="question-div-wrapper">
                        {quizzes?.map((quizz, index) => (
                            <Fragment key={`sentence-${index}`}>
                                <div
                                    className={`small-question-div-outside ${index === selectSentence ? "active" : ""
                                        } ${!quizz.question.trim() || !checkAnswers(quizz.answers) || quizz.correct_answers === 0 ? "error" : ""
                                        }`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setSelectSentence(index)}
                                >
                                    <div className="small-question-div">
                                        Câu {index + 1} <DeleteFilled onClick={(e) => {
                                            e.stopPropagation();
                                            if (quizzes.length === 0) {
                                                setSelectSentence(null);
                                            } else setSelectSentence(selectSentence => Math.min(selectSentence, quizzes.length - 1));
                                            setQuizzes((prev) => {
                                                prev.splice(index, 1);
                                                return [...prev];
                                            });
                                        }} className="delete" />
                                        <img style={{ position: "absolute", right: -5, top: -5 }}
                                            alt="Dễ-TrungBình-Khó" width={13} src={icons[quizz.difficulty]} />
                                    </div>
                                </div>
                            </Fragment>
                        ))}

                        <div
                            className="small-question-div-outside"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setQuizzes((prev) => {
                                    return [
                                        ...prev,
                                        {
                                            answers: new Array(4).fill(""),
                                            correct_answers: [],
                                            question: "",
                                            images: [],
                                        },
                                    ];
                                });
                                setSelectSentence(quizzes?.length);
                            }}
                        >
                            <div className="small-question-div">Thêm</div>
                        </div>
                    </div>
                </Col>
                <Col flex="1" style={{ height: "400px" }}>
                    <div
                        style={{
                            background: "#f0f2f5",
                            padding: "20px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {quizzes.length > 0 ? (
                            <>
                                <div
                                    style={{
                                        flex: "0 0 auto",
                                        marginBottom: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            marginRight: "10px",
                                        }}
                                    >
                                        Câu hỏi
                                    </div>
                                    <div style={{ display: "block", width: "100%", backgroundColor: "white" }}>
                                        <RichEditor value={{
                                            text: quizzes?.[selectSentence]?.question,
                                            images: quizzes?.[selectSentence]?.images,
                                        }} setValue={handleChangeQuestion}/>
                                    </div>
                                    {/* <TextArea
                                        rows={3}
                                        placeholder="Nhập câu hỏi..."
                                        style={{ flex: "1", resize: "none" }}
                                        value={
                                            quizzes?.[selectSentence]?.question
                                        }
                                        onChange={(e) =>
                                            handleChangeQuestion(e.target.value)
                                        }
                                    /> */}
                                </div>

                                <div
                                    style={{
                                        flex: "0 0 auto",
                                        marginBottom: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            marginRight: "10px",
                                        }}
                                    >
                                        Độ khó
                                    </div>
                                    <Select
                                        style={{ flex: "1", resize: "none" }}
                                        value={
                                            quizzes?.[selectSentence]?.difficulty
                                        }
                                        options={difficultyOptions}
                                        onChange={handleChangeDifficulty}
                                    />
                                </div>

                                <div
                                    style={{
                                        flex: "0 0 auto",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: "16px",
                                            fontWeight: "bold",
                                            marginRight: "10px",
                                            flex: "0 0 auto",
                                        }}
                                    >
                                        Đáp án
                                    </div>

                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            flex: "1",
                                        }}
                                    >
                                        {selectSentence >= 0 &&
                                            quizzes?.[
                                                selectSentence
                                            ]?.answers?.map(
                                                (textAnswer, index) => (
                                                    <div
                                                        key={`answer-${index}`}
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                            marginTop: "10px",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                marginRight:
                                                                    "5px",
                                                            }}
                                                        >
                                                            {`${String.fromCharCode(
                                                                65 + index
                                                            )}. `}
                                                        </div>
                                                        <TextArea
                                                            rows={1}
                                                            placeholder="Nhập đáp án..."
                                                            style={{
                                                                flex: "1",
                                                                resize: "none",
                                                            }}
                                                            value={textAnswer}
                                                            onChange={(e) =>
                                                                handleChangeAnswer(
                                                                    index,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        />

                                                        <Checkbox
                                                            style={{
                                                                marginLeft:
                                                                    "10px",
                                                            }}
                                                            checked={(
                                                                quizzes?.[
                                                                selectSentence
                                                                ] || []
                                                            )?.correct_answers?.includes(
                                                                index
                                                            )}
                                                            onChange={() => {
                                                                const newQuizzes =
                                                                    [
                                                                        ...quizzes,
                                                                    ];
                                                                if (
                                                                    newQuizzes?.[
                                                                    selectSentence
                                                                    ]
                                                                ) {
                                                                    if (
                                                                        (
                                                                            newQuizzes?.[
                                                                            selectSentence
                                                                            ] ||
                                                                            []
                                                                        )?.correct_answers?.includes(
                                                                            index
                                                                        )
                                                                    ) {
                                                                        newQuizzes[
                                                                            selectSentence
                                                                        ].correct_answers =
                                                                            newQuizzes[
                                                                                selectSentence
                                                                            ].correct_answers.filter(
                                                                                (
                                                                                    item
                                                                                ) =>
                                                                                    item !==
                                                                                    index
                                                                            );
                                                                    } else {
                                                                        newQuizzes[
                                                                            selectSentence
                                                                        ].correct_answers.push(
                                                                            index
                                                                        );
                                                                    }
                                                                }

                                                                setQuizzes(
                                                                    newQuizzes
                                                                );
                                                            }}
                                                        >
                                                            Đúng
                                                        </Checkbox>
                                                    </div>
                                                )
                                            )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div>
                                Vui lòng thêm câu hỏi, hiện tại không có câu hỏi nào
                            </div>
                        )}
                    </div>
                </Col>
            </Row>
            <div
                style={{
                    marginTop: "20px",
                    justifyContent: "end",
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                }}
            >
                <span style={{ color: "red" }}>
                    ́* Chỉ sử dụng khi không có câu hỏi nào
                </span>
                <Button
                    type="primary"
                    disabled={quizzes?.length > 0}
                    onClick={() => {
                        setIsModalVisible(true);
                    }}
                >
                    Khởi tạo câu hỏi tự động
                </Button>
                <Button type="primary" onClick={handleSubmit}>
                    Lưu
                </Button>
            </div>

            {isModalVisible ? (
                <Modal
                    open={isModalVisible}
                    onCancel={handleCloseModal}
                    footer={null}
                >
                    <div style={{ width: "100%" }}>
                        <Form
                            initialValues={initialValues}
                            form={form}
                            layout="vertical"
                            onFinish={handleInitialAutoTest}
                        >
                            <Form.Item
                                name={"lessons"}
                                label={"Chọn bài học"}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                                rules={[
                                    {
                                        required: true,
                                        message: "Vui lòng chọn bài học",
                                    },
                                ]}
                            >
                                <Select
                                    placeholder={"Chọn bài học"}
                                    mode="multiple"
                                    style={{ width: "100%" }}
                                    options={lessons.map(les => ({
                                        value: les.value,
                                        label: les.label,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item
                                name={"number_of_quiz"}
                                label={"Số lượng câu hỏi"}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                            >
                                <InputNumber placeholder={"10"} />
                            </Form.Item>
                            <Form.Item
                                name={"number_of_multichoice"}
                                label={"Số lượng câu hỏi nhiều đáp án"}
                                labelCol={{ span: 24 }}
                                wrapperCol={{ span: 24 }}
                            >
                                <InputNumber placeholder={"2"} />
                            </Form.Item>
                            <Form.Item style={{ textAlign: "right" }}>
                                <Button type="primary" htmlType="submit">
                                    Tạo bài kiểm tra tự động
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </Modal>
            ) : (
                <></>
            )}
        </div>
    );
}

export default QuizzTest;
